import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useConfirm } from '../../components/ConfirmDialog';
import Modal from '../../components/Modal';
import type { UserAccess, UserRole, UserAccessStatus } from '../../types';

const STATUS_LABEL: Record<UserAccessStatus, { label: string; cls: string }> = {
  approved:       { label: 'Approved', cls: 'badge-approved' },
  pending:        { label: 'Pending',  cls: 'badge-review'   },
  rejected:       { label: 'Rejected', cls: 'badge-error'    },
  invited:        { label: 'Invited',  cls: 'badge-invited'  },
  not_registered: { label: 'Unknown',  cls: 'badge-review'   },
};

const ROLE_LABEL: Record<UserRole, string> = {
  admin:  'Admin',
  member: 'Member',
};

export default function UserManagementPage() {
  const { instance }    = useMsal();
  const cfg              = useRuntimeConfig();
  const { userId, isAdmin } = useUserRole();
  const api               = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const confirm            = useConfirm();

  const [users, setUsers]               = useState<UserAccess[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch]     = useState('');
  const [patchingUser, setPatchingUser] = useState<string | null>(null);
  const [error, setError]               = useState('');

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]   = useState<UserRole>('member');
  const [inviting, setInviting]       = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/settings/profile" replace />;

  async function loadUsers() {
    setUsersLoading(true);
    try {
      const list = await api.getAdminUsers();
      setUsers(list);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUsersLoading(false);
    }
  }

  async function handlePatchUser(targetUserId: string, payload: { role?: UserRole; status?: UserAccessStatus }) {
    setPatchingUser(targetUserId);
    try {
      const updated = await api.patchAdminUser(targetUserId, payload);
      setUsers(prev => prev.map(u => u.userId === targetUserId ? updated : u));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPatchingUser(null);
    }
  }

  async function handleDeleteUser(targetUserId: string) {
    setPatchingUser(targetUserId);
    try {
      await api.deleteOrgMember(targetUserId);
      setUsers(prev => prev.filter(u => u.userId !== targetUserId));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPatchingUser(null);
    }
  }

  // Fully deletes the user's row (unlike Revoke, which only sets status to
  // "rejected"). If they visit the portal again, they start over at the
  // "Request access" screen.
  async function handleRemoveUser(u: UserAccess) {
    const ok = await confirm(
      `Remove ${u.name || u.email} from this organisation? Their access record will be deleted entirely — if they open the portal again, they'll need to request access from scratch.`,
      { title: 'Remove user', confirmLabel: 'Remove', danger: true },
    );
    if (!ok) return;
    await handleDeleteUser(u.userId);
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const added = await api.inviteUser(inviteEmail.trim().toLowerCase(), inviteRole);
      setUsers(prev => [added, ...prev]);
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('member');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  }

  const filteredUsers = users.filter(u => {
    const q = userSearch.toLowerCase();
    return !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  return (
    <div>
      <Link to="/settings" className="settings-back-link">← Organisation Settings</Link>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="settings-form">
        <div className="settings-card">
          <div className="row-flex-between mb-sm">
            <div>
              <h2 className="settings-section-title">User Management</h2>
              <p className="settings-section-desc" style={{ margin: 0 }}>
                Approve or reject users who request access. Assign the Admin role to trusted team members.
              </p>
            </div>
            <div className="row-flex">
              <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(true)}>
                + Add user
              </button>
              <button className="btn btn-outline btn-sm" onClick={loadUsers} disabled={usersLoading}>
                {usersLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="form-group mb-sm">
            <input
              className="form-input"
              placeholder="Search by name or email…"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
            />
          </div>

          {usersLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}><span className="spinner" /></div>
          ) : filteredUsers.length === 0 ? (
            <p style={{ color: 'var(--t2)', textAlign: 'center', padding: '2rem 0' }}>
              {userSearch ? 'No users match your search.' : 'No users registered yet.'}
            </p>
          ) : (
            <div className="users-table-wrapper">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => {
                    const isSelf     = u.userId === userId;
                    const isPatching = patchingUser === u.userId;
                    const statusBadge = STATUS_LABEL[u.status] ?? STATUS_LABEL.pending;
                    return (
                      <tr key={u.userId}>
                        <td>
                          <div className="row-flex">
                            <span className="avatar" title={u.email}>
                              {(u.name || u.email)[0]?.toUpperCase() ?? '?'}
                            </span>
                            <div>
                              <div style={{ fontWeight: 500 }}>{u.name || '—'}</div>
                              <div className="hint-text">{u.email}</div>
                            </div>
                            {isSelf && <span className="you-badge">You</span>}
                          </div>
                        </td>
                        <td>
                          <span style={{ fontWeight: 500 }}>{ROLE_LABEL[u.role]}</span>
                        </td>
                        <td>
                          <span className={`badge ${statusBadge.cls}`}>{statusBadge.label}</span>
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--t2)' }}>
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {u.status === 'invited' ? (
                              /* Invited users: only cancel option */
                              <button
                                className="btn btn-sm btn-outline"
                                disabled={isPatching}
                                onClick={() => handleDeleteUser(u.userId)}
                              >
                                Cancel invite
                              </button>
                            ) : (
                              <>
                                {/* Approve */}
                                {u.status !== 'approved' && (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    disabled={isPatching}
                                    onClick={() => handlePatchUser(u.userId, { status: 'approved' })}
                                  >
                                    Approve
                                  </button>
                                )}
                                {/* Revoke */}
                                {u.status === 'approved' && !isSelf && (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    disabled={isPatching}
                                    onClick={() => handlePatchUser(u.userId, { status: 'rejected' })}
                                  >
                                    Revoke
                                  </button>
                                )}
                                {/* Promote / Demote */}
                                {u.role === 'member' && u.status === 'approved' && (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    disabled={isPatching}
                                    onClick={() => handlePatchUser(u.userId, { role: 'admin' })}
                                  >
                                    Make Admin
                                  </button>
                                )}
                                {u.role === 'admin' && !isSelf && (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    disabled={isPatching}
                                    onClick={() => handlePatchUser(u.userId, { role: 'member' })}
                                  >
                                    Remove Admin
                                  </button>
                                )}
                                {/* Remove: fully deletes the row, unlike Revoke */}
                                {!isSelf && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    disabled={isPatching}
                                    onClick={() => handleRemoveUser(u)}
                                  >
                                    Remove
                                  </button>
                                )}
                              </>
                            )}
                            {isPatching && <span className="spinner" style={{ width: 16, height: 16 }} />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showInvite && (
        <Modal
          title="Add user"
          onClose={() => setShowInvite(false)}
          width={420}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setShowInvite(false)}>
                Cancel
              </button>
              <button type="submit" form="invite-user-form" className="btn btn-primary" disabled={inviting}>
                {inviting ? 'Adding…' : 'Add user'}
              </button>
            </>
          }
        >
          <form id="invite-user-form" className="stack-gap-sm" onSubmit={handleInvite}>
            <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>
              Enter the user's FINT email. When they next log in they will be automatically approved
              with the role you choose — no request needed.
            </p>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input
                className="form-input"
                type="email"
                placeholder="user@fintinc.com"
                required
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-input"
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as UserRole)}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
